/**
 * The synced preview's rules (spec 2026-10-23 §5 Tab 2: "a player that
 * highlights each paragraph as Abby reads it — the same reveal the member
 * sees"). The phone reveals paragraph i when the position passes its startMs
 * (apps/mobile/hooks/useWelcomeAudioSync.ts); the admin preview highlights the
 * paragraph being read by the same timing.
 *
 * The length is ALWAYS `timing.totalDurationMs`. The assembled file has no
 * Xing header and mixes bit rates, so a player mis-estimates its duration
 * (backend/src/app-experience/tts.ts assembleWelcome). The player's own
 * duration is never read.
 */
import type { WelcomeTiming } from './contract';

/**
 * The paragraph being read at `tMs`: i while startMs(i) ≤ t < startMs(i+1);
 * the last one while t < totalDurationMs. -1 before the first start and from
 * totalDurationMs on.
 */
export function activeParagraph(timing: WelcomeTiming, tMs: number): number {
  const ps = timing.paragraphs;
  if (ps.length === 0 || !(tMs >= ps[0].startMs) || tMs >= timing.totalDurationMs) return -1;
  let active = -1;
  for (let i = 0; i < ps.length; i++) {
    if (ps[i].startMs <= tMs) active = i;
    else break;
  }
  return active;
}

/** The player's position in seconds → ms, kept within 0 … totalDurationMs. */
export function positionOf(currentTimeSeconds: number, totalDurationMs: number): number {
  if (!Number.isFinite(currentTimeSeconds) || currentTimeSeconds <= 0) return 0;
  return Math.min(totalDurationMs, Math.round(currentTimeSeconds * 1000));
}

/** m:ss, rounded down like a player clock. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
